import { EJHEALTH_ARTICLE_ROUTE } from "@/lib/routes";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";
import { SidebarThumb } from "../sidebar-thumb";
import type { SidebarItem } from "@/fixtures/ej-health/fixtures";

/**
 * The tinted list card that every rail carries — 最新文章, 作者其他文章.
 *
 * It was copied into four files (the listing rails, the article page, the home
 * hero), which is how the same card ended up with three different item counts
 * and, after 熱門專欄 was renamed, would have ended up with two different
 * headings. One component, so a change lands everywhere at once.
 */
export function SidebarListCard({
  title,
  items,
  count = 6,
}: {
  title: string;
  items: SidebarItem[];
  count?: number;
}) {
  const rows = items.slice(0, count);
  return (
    <aside
      style={{ backgroundColor: EJHEALTH_COLORS.primarySoft, padding: 24 }}
    >
      <div
        className="flex items-end justify-between pb-3"
        style={{ borderBottom: `1px solid ${EJHEALTH_COLORS.primaryRule}` }}
      >
        <span
          style={{
            ...EJHEALTH_TYPE.eyebrowLg,
            color: EJHEALTH_COLORS.primary,
          }}
        >
          {title}
        </span>
      </div>
      {/* Tablet lays the rows TWO across.

          Below `lg` the rail is full width (QA, previous round), and a
          full-width column of single rows left a 705px card holding one 96px
          thumbnail per line with an ocean of space beside it. QA: "change one
          item per row to two items per row, and adjust the separators".

          The separators follow the layout rather than the index: stacked, every
          row but the last carries the rule; two-up, the rule belongs to the
          last ROW (so both items in it drop it, and an odd final item still
          closes the list), and the second column takes a left rule instead of
          sitting flush against the first. At `lg` the rail is 300px again and
          it all reverts to the stacked rules.

          The gutter is SYMMETRIC padding, not a grid gap. With `gap-x-5` plus a
          `pl-5` on the right column only, the two tracks were equal but their
          CONTENTS were not — the right item was 20px narrower, so its thumbnail
          sat further in and its headline wrapped earlier than the left one's
          (measured at 768: 319px against 298px). QA: "the width of the article
          item on left and right are not consistent". Half the gutter on each
          side of the rule leaves both contents the same width and the rule
          centred between them. */}
      <div
        data-sidebar-list
        className="flex flex-col md:grid md:grid-cols-2 lg:flex lg:flex-col"
        style={{ ["--ejh-rule" as string]: EJHEALTH_COLORS.divider }}
      >
        {rows.map((item, i) => {
          const lastStacked = i === rows.length - 1;
          // Index of the first item on the final two-up row.
          const lastRowStart = rows.length - (rows.length % 2 === 0 ? 2 : 1);
          const inLastRow = i >= lastRowStart;
          return (
            <div
              key={item.title + i}
              className={[
                "border-[color:var(--ejh-rule)]",
                lastStacked ? "" : "border-b",
                inLastRow ? "md:border-b-0" : "md:border-b",
                // The left column carries a TRANSPARENT right border so both
                // items spend the same 1px on a border box; without it the
                // divider took its pixel out of the right item alone and the
                // two contents measured 309 against 308.
                "md:odd:border-r md:odd:border-r-transparent md:odd:pr-5",
                "md:even:border-l md:even:pl-5",
                lastStacked ? "lg:border-b-0" : "lg:border-b",
                // The `lg` reset needs the SAME `even:` pseudo-class to outrank
                // the `md:even:` rule — a plain `lg:border-l-0` is a weaker
                // selector, and both media queries match at 1280, so the column
                // rule leaked into the stacked rail.
                "lg:odd:border-r-0 lg:odd:pr-0",
                "lg:even:border-l-0 lg:even:pl-0",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <SidebarThumb item={item} href={EJHEALTH_ARTICLE_ROUTE} />
            </div>
          );
        })}
      </div>
    </aside>
  );
}
